import {Injectable} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Cart} from '@spartacus/cart/base/root';
import {CxEvent, EventService} from '@spartacus/core';
import {EMPTY, Observable, race, timer} from 'rxjs';
import {defaultIfEmpty, filter, first, map, switchMap, take} from 'rxjs/operators';
import {v4 as uuidV4} from 'uuid';
import {DcpActiveCartService} from '../../dcp-active-cart.service';
import {EntryGroupService} from '@app/product/services/entry-group.service';
import {DcpEddlCartStateService} from './dcp-eddl-cart-state.service';
import {AbstractEddlCartService} from './abstract-eddl-cart.service';
import {TransactionTypeCode} from '@app/services/transactionTypes/transaction-type-code';
import {EddlAddToCartEvent} from './events/eddl-add-to-cart-event';
import {EddlMergedAbstract} from './events/eddl-merged.abstract';
import {EddlCartCreatedEvent} from './events/eddl-cart-created-event';
import {EddlAdobeTrackingData, EddlCollectionState} from './models/eddl-adobe-tracking-data';

// How long the cartAdd builder waits for the active cart to contain the just-added entry before
// falling back to a cart synthesized from event.entry.
//
// Normal flow: the active-cart store reloads with the full cart (incl. add-on entry groups) within
// a few seconds, so we wait up to 5s to track the complete cart.
//
// Upsell flow: the add happens inside an iframe whose JS context is torn down shortly after the add
// (empirically it survives >=500ms but <2000ms), and whose active cart is a different cart than the
// backend upsell cart - so a matching cart never arrives. We must resolve (and synthesize from
// event.entry) well before that teardown, hence the much shorter wait. See getPostAddCart$.
const ADD_TO_CART_CART_WAIT_MS = 5000;
const ADD_TO_CART_CART_WAIT_UPSELL_MS = 500;

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpEddlAddToCartService extends AbstractEddlCartService<EddlAddToCartEvent> {
  // sessionStorage key for upsell cart ids that have already fired cartCreate.
  // Must survive iframe teardown (each upsell opens a new iframe = fresh Angular
  // injector, so an in-memory Set resets on every add). sessionStorage is shared
  // across same-origin iframes and the outer page within a browser session (DCP-61998).
  private static readonly SEEN_UPSELL_CARTS_KEY = 'dcp.eddl.seenUpsellCarts';
  constructor(
    protected eventService: EventService,
    protected eddlCartStateService: DcpEddlCartStateService,
    protected activeCartService: DcpActiveCartService,
    protected entryGroupService: EntryGroupService
  ) {
    super(eventService, eddlCartStateService, activeCartService);
  }

  protected getEventName(): string {
    return 'cartAdd';
  }

  getData(event: CxEvent): Observable<EddlAdobeTrackingData> {
    if (!(event instanceof EddlAddToCartEvent) || !(event instanceof EddlMergedAbstract)) {
      return EMPTY;
    }
    if (event.productCode) {
      // CPS Product case
      return this.getPostAddCart$(event).pipe(
        untilDestroyed(this),
        map((cart) => {
          if (this.isNewlyCreatedCart(cart, event.productCode)) {
            event.collectState = EddlCollectionState.START;
            event.collectToken = uuidV4();
          }
          this.triggerEddlCartCreatedEvent(cart, event);
          event.cart = cart;
          const allEntries = this.collectEntriesForEvent(event, cart);
          return this.fillData(event, allEntries, cart);
        })
      );
    }
    return this.getLegacyCartAddData$(event);
  }

  /**
   * Legacy add-to-cart tracking. A new cart is detected when the resolved cart has no relevant
   * entries (OOTB heuristic) OR the cart was empty BEFORE this add.
   *
   * Upsell: the active-cart store in the iframe holds a different, local cart than the backend
   * upsell cart. The add response does not expose the new backend cart id. We use the pre-request
   * cart id (backfilled onto event.cart.guid) as a dedup key persisted in sessionStorage: the
   * first upsell add with a given local cart id fires cartCreate; subsequent adds in the same
   * browser session with the same id are suppressed — even across iframe teardown/recreation
   * (each upsell iframe is a fresh Angular injector; an in-memory Set would reset every time).
   * A new local cart id (after deletion/recreation) correctly fires cartCreate again (DCP-61998).
   *
   * Non-upsell: the pre-add snapshot is the reliable new-cart signal because the synthesized
   * fallback cart always contains event.entry and therefore looks non-empty.
   */
  private getLegacyCartAddData$(event: EddlAddToCartEvent): Observable<EddlAdobeTrackingData> {
    const isUpsell = this.isUpsellEvent(event);
    return this.activeCartService.getActiveCart().pipe(
      take(1),
      defaultIfEmpty(null as Cart),
      map((preAddCart) => (this.eddlCartStateService.getRelevantCartEntries(preAddCart?.entries) ?? []).length === 0),
      switchMap((preAddCartWasEmpty) =>
        this.getPostAddCart$(event).pipe(
          map((cart) => {
            const entries = this.eddlCartStateService.getRelevantCartEntries(cart.entries);
            let isNewCart: boolean;
            if (isUpsell) {
              const cartKey = event.cart?.guid;
              isNewCart = !cartKey || !this.hasSeenUpsellCart(cartKey);
              if (cartKey && isNewCart) {
                this.markUpsellCartSeen(cartKey);
              }
            } else {
              isNewCart = !entries || entries.length === 0 || preAddCartWasEmpty;
            }
            if (isNewCart) {
              event.collectState = EddlCollectionState.START;
              event.collectToken = uuidV4();
            }
            this.triggerEddlCartCreatedEvent(cart, event);
            event.cart = cart;
            const allEntries = this.collectEntriesForEvent(event, cart);
            return this.fillData(event, allEntries, cart);
          })
        )
      ),
      untilDestroyed(this)
    );
  }

  /**
   * Resolves the cart used to build the cartAdd payload, racing two sources:
   *
   *  1. matchingCart$ - the first active-cart emission that already contains the just-added entry.
   *     The OOTB cart store is only replaced after the async post-add reload, so the first
   *     emissions are the stale pre-add cart; we filter for the one that contains the addition.
   *
   *  2. fallbackCart$ - fires unconditionally after ADD_TO_CART_CART_WAIT_MS and resolves to the
   *     current cart (if it contains the addition) or a cart synthesized from `event.entry`.
   *
   * Why a timer-based race instead of `getActiveCart().pipe(filter, first, timeout)`:
   * in the upsell flow the add happens in an iframe whose JS context can be torn down before the
   * normal 5s fallback deadline, and the Spartacus active-cart store holds a different, local cart
   * than the backend upsell cart - its entry numbers never match the add response, so the matching
   * cart never arrives. The old `timeout()` was downstream of `filter()`, so non-matching active-cart
   * emissions did not reset it; without a match it would fall back only after the configured timeout.
   * That is too late for upsell, and the old fallback used the current local cart rather than a cart
   * synthesized from the authoritative addToCartV2 `event.entry` (product.data w/ Canonical_ID +
   * totalPrice). Racing an independent `timer` lets upsell use a short deadline and still preserves
   * the DCP-51934 no-hang guarantee by always emitting a matching or synthesized cart.
   */
  private getPostAddCart$(event: EddlAddToCartEvent): Observable<Cart> {
    // Primary: the first active-cart emission that already contains the just-added entry.
    const matchingCart$ = this.activeCartService.getActiveCart().pipe(
      filter((cart) => this.cartContainsAddition(cart, event)),
      first()
    );

    // Fallback that ALWAYS fires after a fixed delay, independent of how often the active cart
    // stream emits. The previous `timeout()`-after-`filter()` approach never fired in the upsell
    // flow: the active cart keeps emitting non-matching carts (a different, local cart than the
    // backend upsell cart - entry numbers never line up), and `timeout` resets on each upstream
    // emission, so the matching cart never arrives and the fallback never triggers → the cartAdd
    // event hung forever (DCP-61998). A timer-based race guarantees the event is always produced.
    // The upsell flow uses a short wait so the fallback resolves before the iframe is torn down;
    // every other flow keeps the long wait so the full reloaded cart (incl. add-ons) is tracked.
    const waitMs = this.isUpsellEvent(event) ? ADD_TO_CART_CART_WAIT_UPSELL_MS : ADD_TO_CART_CART_WAIT_MS;
    const fallbackCart$ = timer(waitMs).pipe(
      switchMap(() => this.activeCartService.getActiveCart().pipe(take(1), defaultIfEmpty(null as Cart))),
      map((cart) => {
        // Resolution order:
        //  1. the current cart if it actually contains the addition;
        //  2. a cart synthesized from event.entry (legacy/upsell add - the authoritative
        //     addToCartV2 response carries product.data w/ Canonical_ID + totalPrice), so the
        //     cartAdd still fires with correct data when the store never loads this cart;
        //  3. the current cart as-is (CPS/productCode add with no event.entry to synthesize from),
        //     preserving the pre-existing "fall back to the current cart" behaviour.
        if (this.cartContainsAddition(cart, event)) {
          return cart;
        }
        if (event.entry) {
          return this.buildCartFromEntry(event);
        }
        return cart;
      })
    );

    return race(matchingCart$, fallbackCart$);
  }

  /**
   * The upsell add-to-cart runs inside an iframe that is torn down shortly after the add, so the
   * cartAdd must be built from event.entry within a short window (see ADD_TO_CART_CART_WAIT_*).
   * The add response entry carries the transaction type, which is the context-independent signal
   * for the upsell flow (ORDER_UPSELL / TDD_UPSELL).
   */
  private isUpsellEvent(event: EddlAddToCartEvent): boolean {
    const transactionType = event.entry?.transactionType;
    return transactionType === TransactionTypeCode.ORDER_UPSELL || transactionType === TransactionTypeCode.TDD_UPSELL;
  }

  private hasSeenUpsellCart(cartKey: string): boolean {
    try {
      const stored = sessionStorage.getItem(DcpEddlAddToCartService.SEEN_UPSELL_CARTS_KEY);
      return !!stored && JSON.parse(stored).includes(cartKey);
    } catch {
      return false;
    }
  }

  private markUpsellCartSeen(cartKey: string): void {
    try {
      const stored = sessionStorage.getItem(DcpEddlAddToCartService.SEEN_UPSELL_CARTS_KEY);
      const seen: string[] = stored ? JSON.parse(stored) : [];
      if (!seen.includes(cartKey)) {
        seen.push(cartKey);
        sessionStorage.setItem(DcpEddlAddToCartService.SEEN_UPSELL_CARTS_KEY, JSON.stringify(seen));
      }
    } catch {
      // sessionStorage unavailable (SSR or private browsing quota) — silently skip dedup
    }
  }

  /**
   * Builds a minimal Cart from the just-added entry so the EDDL cartAdd event can still be
   * populated when the active-cart store never loads a cart containing the addition (upsell iframe
   * flow - DCP-61998). initializeTrackingData only needs guid + currency; collectEntriesForEvent
   * falls back to [event.entry] when there are no matching entry groups, so this single entry is
   * sufficient. The cart id comes from event.cart - the producer sets it from the authoritative
   * addToCartV2 cartId/cartCode (see triggerEddlAddToCartEvent). We deliberately do NOT fall back
   * to the current active cart's guid: in the upsell flow that is a different, local cart than the
   * backend upsell cart, so using it would make cartAdd/cartCreate report a foreign cart id.
   */
  private buildCartFromEntry(event: EddlAddToCartEvent): Cart {
    return {
      guid: event.cart?.guid,
      totalPrice: {currencyIso: event.entry?.totalPrice?.currencyIso},
      entries: event.entry ? [event.entry] : [],
      entryGroups: [],
    };
  }

  private cartContainsAddition(cart: Cart, event: EddlAddToCartEvent): boolean {
    if (event.entry?.entryNumber != null) {
      return !!cart?.entries?.some((entry) => entry.entryNumber === event.entry?.entryNumber);
    }
    if (event.productCode) {
      return !!cart?.entries?.some((entry) => entry.product?.code === event.productCode);
    }
    // Legacy add without entry number: wait for a populated cart (SBN express pattern)
    return !!cart?.guid && !!cart?.entries?.length;
  }

  triggerEddlCartCreatedEvent(cart: Cart, parentEvent?: EddlAddToCartEvent): void {
    // If parentEvent has a collectToken, this is a cart creation event
    if (parentEvent?.collectToken) {
      this.triggerEddlCartCreate(cart, parentEvent);
    }
  }

  private triggerEddlCartCreate(cart: Cart, parentEvent?: EddlAddToCartEvent) {
    const event: EddlCartCreatedEvent = new EddlCartCreatedEvent();
    event.cart = cart;
    if (parentEvent && parentEvent instanceof EddlAddToCartEvent) {
      event.collectToken = parentEvent.collectToken;
      event.collectState = EddlCollectionState.FINISH;
    }
    // Defer dispatch to next tick to ensure cart state is fully updated before event processing
    setTimeout(() => this.eventService.dispatch(event, EddlCartCreatedEvent));
  }

  /**
   * Determines if the cart was newly created by checking if all entries belong to the product being added.
   * This handles cases where a product with add-ons creates multiple entries in a single operation.
   *
   * @param cart - The current cart state
   * @param productCode - The product code being added
   * @returns true if this is a cart creation event, false otherwise
   */
  private isNewlyCreatedCart(cart: Cart, productCode: string): boolean {
    // Empty cart is definitely new
    if (!cart?.entries || cart.entries.length === 0) {
      return true;
    }

    // Filter out configurator entries
    const entries = this.eddlCartStateService.getRelevantCartEntries(cart.entries);
    if (!entries || entries.length === 0) {
      return true;
    }

    // Get all root entry groups for the product being added
    const rootGroups = this.entryGroupService?.getRootGroupsByProductCode?.(cart.entryGroups, productCode);
    if (!rootGroups || rootGroups.length === 0) {
      // Fallback when entry groups unavailable:
      // Only treat as cart creation if ALL cart entries belong to this operation
      // (i.e., the cart was empty before this add-to-cart)
      // This handles the add-ons case: multiple entries but still cart creation
      return entries.length > 0 && cart.entries?.length === entries.length;
    }

    // Count all entries from this product's hierarchy (main + add-ons + editions)
    // IMPORTANT: Apply the same filtering to group entries as we do to cart entries
    const allEntriesFromGroups = this.getAllEntriesFromGroups(rootGroups);

    // Apply the same filtering logic to entries from groups
    const relevantEntriesFromThisProduct = this.eddlCartStateService.getRelevantCartEntries(allEntriesFromGroups);
    const entriesFromThisProductCount = relevantEntriesFromThisProduct.length;

    // If all entries belong to this product, it's cart creation
    return entries.length === entriesFromThisProductCount;
  }
}
